import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

export type PhotoSource = 'camera' | 'library'

export type CapturedPhoto = { blob: Blob; takenAtIso: string | null }

const isUserCancellation = (error: unknown): boolean =>
  error instanceof Error && /cancel/i.test(error.message)

/**
 * Open the native camera or photo library and return the picked image.
 * Returns null ONLY when the user cancels the picker — every other failure
 * (no webPath, unreadable capture) throws so callers can surface it.
 * The plugin transcodes to JPEG on native when `quality` is set.
 */
export async function capturePhoto(
  source: PhotoSource,
): Promise<CapturedPhoto | null> {
  // The cancellation guard covers ONLY the picker call: a post-capture fetch
  // rejected with a "cancelled" message (WKWebView aborts) is a real failure,
  // not the user backing out.
  let photo: Awaited<ReturnType<typeof Camera.getPhoto>>
  try {
    photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
      quality: 90,
      correctOrientation: true,
    })
  } catch (error) {
    if (isUserCancellation(error)) return null
    throw error
  }

  // A resolved plugin call with no webPath is a plugin failure, not a cancel
  if (!photo.webPath) {
    throw new Error('camera plugin returned no webPath for the capture')
  }
  const response = await fetch(photo.webPath)
  if (!response.ok) {
    throw new Error(`photo fetch failed with status ${response.status}`)
  }
  const blob = await response.blob()
  // Library picks were shot at an unknown earlier time — only a fresh
  // camera capture can claim "taken now"; createdAt covers record time.
  const takenAtIso = source === 'camera' ? new Date().toISOString() : null
  return { blob, takenAtIso }
}
